import React, { useEffect, useState, useCallback } from "react";
import { wasteApi } from "@/api/waste";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Search, Loader2, FileDown } from "lucide-react";
import EmptyState from "@/components/shared/EmptyState";

const formatEur = (v) => `${(v || 0).toFixed(2)} €`;

export default function WasteHistory({ venueId, settings, refreshKey }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [family, setFamily] = useState("");
  const [reason, setReason] = useState("");
  const [zone, setZone] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const items = await wasteApi.listRecords({
        venue_id: venueId,
        product_name: search || undefined,
        family: family || undefined,
        reason: reason || undefined,
        zone: zone || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setRecords(items || []);
    } catch (e) {
      toast.error("Error carregant historial");
    } finally {
      setLoading(false);
    }
  }, [venueId, search, family, reason, zone, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar aquest registre?")) return;
    try {
      await wasteApi.deleteRecord(id);
      toast.success("Registre eliminat");
      load();
    } catch (e) {
      toast.error("Error eliminant");
    }
  };

  const exportCsv = () => {
    if (!records.length) return;
    const headers = ["Data", "Producte", "Família", "Motiu", "Zona", "Quantitat", "Unitat", "Cost €", "Notes", "Registrat per"];
    const lines = [headers.join(",")];
    records.forEach((r) => {
      const row = [
        r.date, JSON.stringify(r.product_name || ""), r.family, r.reason, r.zone,
        r.quantity, r.unit, r.total_cost, JSON.stringify(r.notes || ""), r.created_by,
      ].join(",");
      lines.push(row);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `malbaratament_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const families = settings?.product_families || [];
  const reasons = settings?.waste_reasons || [];
  const zones = settings?.zones || [];

  const reasonLabel = (code) => reasons.find((r) => r.code === code)?.label || code;
  const familyLabel = (code) => families.find((f) => f.code === code)?.label || code;
  const zoneLabel = (code) => zones.find((z) => z.code === code)?.label || code;

  return (
    <div className="space-y-4" data-testid="waste-history">
      <Card className="p-4 grid grid-cols-2 lg:grid-cols-6 gap-2 items-end">
        <div className="col-span-2">
          <label className="text-xs font-semibold text-gray-700 block mb-1">Cercar producte</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="waste-search-input"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Tomàquet, pa…" className="pl-9"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Família</label>
          <Select value={family || "all"} onValueChange={(v) => setFamily(v === "all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Totes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Totes</SelectItem>
              {families.map((f) => <SelectItem key={f.code} value={f.code}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Motiu</label>
          <Select value={reason || "all"} onValueChange={(v) => setReason(v === "all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Tots" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tots</SelectItem>
              {reasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Des de</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Fins a</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="col-span-2 lg:col-span-6 flex items-center gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => {
            setSearch(""); setFamily(""); setReason(""); setZone(""); setDateFrom(""); setDateTo("");
          }} data-testid="waste-filters-clear">Netejar filtres</Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!records.length} data-testid="waste-export-csv">
            <FileDown className="w-4 h-4 mr-2" /> Exporta CSV
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#7B2D3F]" /></div>
      ) : records.length === 0 ? (
        <EmptyState icon={Trash2} title="Cap registre" description="No hi ha registres amb aquests filtres." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Producte</th>
                  <th className="px-3 py-2 text-left">Família</th>
                  <th className="px-3 py-2 text-left">Motiu</th>
                  <th className="px-3 py-2 text-left">Zona</th>
                  <th className="px-3 py-2 text-right">Quantitat</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.waste_record_id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.date}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{r.product_name}</td>
                    <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{familyLabel(r.family)}</Badge></td>
                    <td className="px-3 py-2 text-gray-700">{reasonLabel(r.reason)}</td>
                    <td className="px-3 py-2 text-gray-700">{zoneLabel(r.zone)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{(r.quantity || 0).toFixed(2)} {r.unit}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#7B2D3F] tabular-nums">{formatEur(r.total_cost)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="icon" variant="ghost"
                        className="h-7 w-7 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(r.waste_record_id)}
                        data-testid={`waste-delete-${r.waste_record_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
